import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Save, X, Download, Search, Upload } from 'lucide-react';
import { exportToExcel, formatMasterDataForExport } from '../utils/excelExport';

export default function MasterData() {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('branches');
  const [settings, setSettings] = useState(null);
  const [reviewMessageTemplate, setReviewMessageTemplate] = useState('');
  const [branches, setBranches] = useState([]);
  const [theaters, setTheaters] = useState([]);
  const [theaterSlots, setTheaterSlots] = useState([]);
  const [cakes, setCakes] = useState([]);
  const [decorations, setDecorations] = useState([]);
  const [foodItems, setFoodItems] = useState([]);
  const [scanners, setScanners] = useState([]);

  // Dialog states
  const [branchDialog, setBranchDialog] = useState(false);
  const [theaterDialog, setTheaterDialog] = useState(false);
  const [slotDialog, setSlotDialog] = useState(false);
  const [cakeDialog, setCakeDialog] = useState(false);
  const [decorationDialog, setDecorationDialog] = useState(false);
  const [foodDialog, setFoodDialog] = useState(false);
  const [scannerDialog, setScannerDialog] = useState(false);
  const [importDialog, setImportDialog] = useState(false);

  // Import states
  const [importBranch, setImportBranch] = useState('');
  const [importText, setImportText] = useState('');

  // Form states
  const [branchForm, setBranchForm] = useState({ 
    id: '', 
    name: '', 
    location: '', 
    google_review_link: '', 
    state: '', 
    gstin: '', 
    branch_code: '',
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: ''
  });
  const [theaterForm, setTheaterForm] = useState({ id: '', name: '', branch_id: '', branch_name: '', min_capacity: 2, price: 0, extra_person_charge: 0 });
  const [slotForm, setSlotForm] = useState({ id: '', theater_name: '', time_slot: '' });
  const [cakeForm, setCakeForm] = useState({ id: '', name: '', branch_id: '', branch_name: '', price: 0 });
  const [decorationForm, setDecorationForm] = useState({ id: '', name: '', branch_id: '', branch_name: '', price: 0 });
  const [foodForm, setFoodForm] = useState({ id: '', name: '', branch_id: '', branch_name: '', price: 0 });
  const [scannerForm, setScannerForm] = useState({ id: '', name: '', branch_id: '', branch_name: '' });

  // Search states for filtering
  const [searches, setSearches] = useState({
    branches: '',
    theaters: '',
    cakes: '',
    decorations: '',
    food: '',
    slots: '',
    scanners: ''
  });

  // Branch filter states
  const [branchFilters, setBranchFilters] = useState({
    theaters: 'all',
    cakes: 'all',
    decorations: 'all',
    food: 'all',
    slots: 'all',
    scanners: 'all'
  });

  useEffect(() => {
    fetchAllData();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
      setReviewMessageTemplate(response.data.review_message_template);
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  const saveSettings = async () => {
    try {
      await api.put('/settings', null, {
        params: { review_message_template: reviewMessageTemplate }
      });
      toast.success('Settings saved successfully');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [branchesRes, theatersRes, cakesRes, decorationsRes, foodRes, scannersRes] = await Promise.all([
        api.get('/branches'),
        api.get('/theaters'),
        api.get('/cakes'),
        api.get('/decorations'),
        api.get('/food-beverages'),
        api.get('/scanners')
      ]);
      setBranches(branchesRes.data);
      setTheaters(theatersRes.data);
      setCakes(cakesRes.data);
      setDecorations(decorationsRes.data);
      setFoodItems(foodRes.data);
      setScanners(scannersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const refreshDataSilently = async () => {
    try {
      const [branchesRes, theatersRes, cakesRes, decorationsRes, foodRes, scannersRes] = await Promise.all([
        api.get('/branches'),
        api.get('/theaters'),
        api.get('/cakes'),
        api.get('/decorations'),
        api.get('/food-beverages'),
        api.get('/scanners')
      ]);
      setBranches(branchesRes.data);
      setTheaters(theatersRes.data);
      setCakes(cakesRes.data);
      setDecorations(decorationsRes.data);
      setFoodItems(foodRes.data);
      setScanners(scannersRes.data);
    } catch (error) {
      toast.error('Failed to refresh data');
    }
  };

  const fetchTheaterSlots = async () => {
    try {
      const allSlots = [];
      for (const theater of theaters) {
        const response = await api.get(`/theater-slots/${theater.name}`);
        allSlots.push(...response.data);
      }
      setTheaterSlots(allSlots);
    } catch (error) {
      toast.error('Failed to fetch theater slots');
    }
  };

  // Branch CRUD
  const saveBranch = async () => {
    try {
      if (branchForm.id) {
        await api.put(`/master/branches/${branchForm.id}`, branchForm);
        toast.success('Branch updated successfully');
      } else {
        await api.post('/master/branches', { ...branchForm, id: crypto.randomUUID() });
        toast.success('Branch created successfully');
      }
      setBranchDialog(false);
      setBranchForm({ id: '', name: '', location: '', google_review_link: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save branch');
    }
  };

  const deleteBranch = async (id) => {
    if (!window.confirm('Are you sure? This will affect all related data.')) return;
    try {
      await api.delete(`/master/branches/${id}`);
      setBranches(prev => prev.filter(b => b.id !== id));
      toast.success('Branch deleted successfully');
    } catch (error) {
      toast.error('Failed to delete branch');
    }
  };

  // Theater CRUD
  const saveTheater = async () => {
    try {
      const branch = branches.find(b => b.id === theaterForm.branch_id);
      const theaterData = { ...theaterForm, branch_name: branch?.name || '' };
      
      if (theaterForm.id) {
        await api.put(`/master/theaters/${theaterForm.id}`, theaterData);
        toast.success('Theater updated successfully');
      } else {
        await api.post('/master/theaters', { ...theaterData, id: crypto.randomUUID() });
        toast.success('Theater created successfully');
      }
      setTheaterDialog(false);
      setTheaterForm({ id: '', name: '', branch_id: '', branch_name: '', min_capacity: 2, price: 0, extra_person_charge: 0 });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save theater');
    }
  };

  const deleteTheater = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await api.delete(`/master/theaters/${id}`);
      setTheaters(prev => prev.filter(t => t.id !== id));
      toast.success('Theater deleted successfully');
    } catch (error) {
      toast.error('Failed to delete theater');
    }
  };

  // Theater Slot CRUD
  const saveSlot = async () => {
    try {
      await api.post('/master/theater-slots', { ...slotForm, id: crypto.randomUUID() });
      toast.success('Time slot added successfully');
      setSlotDialog(false);
      setSlotForm({ id: '', theater_name: '', time_slot: '' });
      fetchTheaterSlots();
    } catch (error) {
      toast.error('Failed to save time slot');
    }
  };

  const deleteSlot = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await api.delete(`/master/theater-slots/${id}`);
      setTheaterSlots(prev => prev.filter(s => s.id !== id));
      toast.success('Time slot deleted successfully');
    } catch (error) {
      toast.error('Failed to delete time slot');
    }
  };

  // Cake CRUD
  const saveCake = async () => {
    try {
      const branch = branches.find(b => b.id === cakeForm.branch_id);
      const cakeData = { ...cakeForm, branch_name: branch?.name || '' };
      
      if (cakeForm.id) {
        await api.put(`/master/cakes/${cakeForm.id}`, cakeData);
        toast.success('Cake updated successfully');
      } else {
        await api.post('/master/cakes', { ...cakeData, id: crypto.randomUUID() });
        toast.success('Cake created successfully');
      }
      setCakeDialog(false);
      setCakeForm({ id: '', name: '', branch_id: '', branch_name: '', price: 0 });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save cake');
    }
  };

  const deleteCake = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await api.delete(`/master/cakes/${id}`);
      setCakes(prev => prev.filter(c => c.id !== id));
      toast.success('Cake deleted successfully');
    } catch (error) {
      toast.error('Failed to delete cake');
    }
  };

  // Decoration CRUD
  const saveDecoration = async () => {
    try {
      const branch = branches.find(b => b.id === decorationForm.branch_id);
      const decorationData = { ...decorationForm, branch_name: branch?.name || '' };
      
      if (decorationForm.id) {
        await api.put(`/master/decorations/${decorationForm.id}`, decorationData);
        toast.success('Decoration updated successfully');
      } else {
        await api.post('/master/decorations', { ...decorationData, id: crypto.randomUUID() });
        toast.success('Decoration created successfully');
      }
      setDecorationDialog(false);
      setDecorationForm({ id: '', name: '', branch_id: '', branch_name: '', price: 0 });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save decoration');
    }
  };

  const deleteDecoration = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await api.delete(`/master/decorations/${id}`);
      setDecorations(prev => prev.filter(d => d.id !== id));
      toast.success('Decoration deleted successfully');
    } catch (error) {
      toast.error('Failed to delete decoration');
    }
  };

  // Food & Beverage CRUD
  const saveFoodItem = async () => {
    try {
      const branch = branches.find(b => b.id === foodForm.branch_id);
      const foodData = { ...foodForm, branch_name: branch?.name || '' };
      
      if (foodForm.id) {
        await api.put(`/master/food-beverages/${foodForm.id}`, foodData);
        toast.success('Food item updated successfully');
      } else {
        await api.post('/master/food-beverages', { ...foodData, id: crypto.randomUUID() });
        toast.success('Food item created successfully');
      }
      setFoodDialog(false);
      setFoodForm({ id: '', name: '', branch_id: '', branch_name: '', price: 0 });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save food item');
    }
  };

  const deleteFoodItem = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await api.delete(`/master/food-beverages/${id}`);
      setFoodItems(prev => prev.filter(f => f.id !== id));
      toast.success('Food item deleted successfully');
    } catch (error) {
      toast.error('Failed to delete food item');
    }
  };

  // Scanner CRUD
  const saveScanner = async () => {
    try {
      if (!scannerForm.name || !scannerForm.branch_id) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      const branch = branches.find(b => b.id === scannerForm.branch_id);
      const scannerData = { ...scannerForm, branch_name: branch?.name || '' };
      
      if (scannerForm.id) {
        await api.put(`/master/scanners/${scannerForm.id}`, scannerData);
        toast.success('Scanner updated successfully');
      } else {
        await api.post('/master/scanners', { ...scannerData, id: crypto.randomUUID() });
        toast.success('Scanner added successfully');
      }
      setScannerDialog(false);
      setScannerForm({ id: '', name: '', branch_id: '', branch_name: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save scanner');
    }
  };

  const deleteScanner = async (id) => {
    console.log('deleteScanner function called with ID:', id);
    const confirmed = window.confirm('Are you sure you want to delete this scanner?');
    console.log('User confirmation:', confirmed);
    if (!confirmed) return;
    
    try {
      console.log('Sending DELETE request for scanner:', id);
      const response = await api.delete(`/master/scanners/${id}`);
      console.log('DELETE response:', response);
      
      // Immediately update local state
      setScanners(prev => {
        console.log('Previous scanners:', prev);
        const newScanners = prev.filter(s => s.id !== id);
        console.log('New scanners after filter:', newScanners);
        return newScanners;
      });
      
      toast.success('Scanner deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete scanner: ' + (error.message || 'Unknown error'));
    }
  };

  // Export functions
  const exportBranches = () => {
    try {
      const formattedData = formatMasterDataForExport(branches, 'branches');
      exportToExcel(formattedData, 'branches', 'Branches');
      toast.success('Branches exported successfully');
    } catch (error) {
      toast.error('Failed to export branches');
    }
  };

  const exportTheaters = () => {
    try {
      const formattedData = formatMasterDataForExport(theaters, 'theaters');
      exportToExcel(formattedData, 'theaters', 'Theaters');
      toast.success('Theaters exported successfully');
    } catch (error) {
      toast.error('Failed to export theaters');
    }
  };

  const exportCakes = () => {
    try {
      const formattedData = formatMasterDataForExport(cakes, 'cakes');
      exportToExcel(formattedData, 'cakes', 'Cakes');
      toast.success('Cakes exported successfully');
    } catch (error) {
      toast.error('Failed to export cakes');
    }
  };

  const exportDecorations = () => {
    try {
      const formattedData = formatMasterDataForExport(decorations, 'decorations');
      exportToExcel(formattedData, 'decorations', 'Decorations');
      toast.success('Decorations exported successfully');
    } catch (error) {
      toast.error('Failed to export decorations');
    }
  };

  const exportFood = () => {
    try {
      const formattedData = formatMasterDataForExport(foodItems, 'food');
      exportToExcel(formattedData, 'food_beverages', 'Food & Beverages');
      toast.success('Food & Beverages exported successfully');
    } catch (error) {
      toast.error('Failed to export food items');
    }
  };

  const handleImport = async () => {
    try {
      if (!importBranch) {
        toast.error('Please select a branch');
        return;
      }

      if (!importText.trim()) {
        toast.error('Please paste items to import');
        return;
      }

      // Parse the text input
      const lines = importText.split('\n').filter(line => line.trim());
      const items = [];
      
      for (const line of lines) {
        const parts = line.split(',');
        if (parts.length >= 2) {
          const name = parts[0].trim();
          const price = parseFloat(parts[1].trim());
          
          if (name && !isNaN(price)) {
            items.push({ name, price });
          }
        }
      }

      if (items.length === 0) {
        toast.error('No valid items found. Format: Name, Price (one per line)');
        return;
      }

      // Call bulk import API
      await api.post('/master/food-beverages/bulk-import', {
        branch_id: importBranch,
        items: items
      });

      toast.success(`Successfully imported ${items.length} items`);
      setImportDialog(false);
      setImportText('');
      setImportBranch('');
      
      // Refresh food items
      const foodRes = await api.get('/food-beverages');
      setFoodItems(foodRes.data);
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import items');
    }
  };

  // Filtered data - using useMemo to prevent unnecessary re-calculations
  const filteredBranches = useMemo(() => 
    branches.filter(b => 
      b.name.toLowerCase().includes(searches.branches.toLowerCase()) ||
      b.location.toLowerCase().includes(searches.branches.toLowerCase())
    ), [branches, searches.branches]
  );

  const filteredTheaters = useMemo(() => 
    theaters.filter(t => {
      const matchesSearch = t.name.toLowerCase().includes(searches.theaters.toLowerCase()) ||
        t.branch_name.toLowerCase().includes(searches.theaters.toLowerCase());
      const matchesBranch = branchFilters.theaters === 'all' || t.branch_id === branchFilters.theaters;
      return matchesSearch && matchesBranch;
    }), [theaters, searches.theaters, branchFilters.theaters]
  );

  const filteredCakes = useMemo(() => 
    cakes.filter(c => {
      const matchesSearch = c.name.toLowerCase().includes(searches.cakes.toLowerCase()) ||
        c.branch_name.toLowerCase().includes(searches.cakes.toLowerCase());
      const matchesBranch = branchFilters.cakes === 'all' || c.branch_id === branchFilters.cakes;
      return matchesSearch && matchesBranch;
    }), [cakes, searches.cakes, branchFilters.cakes]
  );

  const filteredDecorations = useMemo(() => 
    decorations.filter(d => {
      const matchesSearch = d.name.toLowerCase().includes(searches.decorations.toLowerCase()) ||
        d.branch_name.toLowerCase().includes(searches.decorations.toLowerCase());
      const matchesBranch = branchFilters.decorations === 'all' || d.branch_id === branchFilters.decorations;
      return matchesSearch && matchesBranch;
    }), [decorations, searches.decorations, branchFilters.decorations]
  );

  const filteredFoodItems = useMemo(() => 
    foodItems.filter(f => {
      const matchesSearch = f.name.toLowerCase().includes(searches.food.toLowerCase()) ||
        f.branch_name.toLowerCase().includes(searches.food.toLowerCase());
      const matchesBranch = branchFilters.food === 'all' || f.branch_id === branchFilters.food;
      return matchesSearch && matchesBranch;
    }), [foodItems, searches.food, branchFilters.food]
  );

  const filteredSlots = useMemo(() => 
    theaterSlots.filter(s => {
      const matchesSearch = s.theater_name.toLowerCase().includes(searches.slots.toLowerCase()) ||
        s.time_slot.toLowerCase().includes(searches.slots.toLowerCase());
      
      // For slots, filter by theater's branch
      if (branchFilters.slots !== 'all') {
        const theater = theaters.find(t => t.name === s.theater_name);
        if (!theater || theater.branch_id !== branchFilters.slots) return false;
      }
      return matchesSearch;
    }), [theaterSlots, searches.slots, branchFilters.slots, theaters]
  );

  const filteredScanners = useMemo(() => 
    scanners.filter(s => {
      const matchesSearch = s.name.toLowerCase().includes(searches.scanners.toLowerCase()) ||
        (s.branch_name && s.branch_name.toLowerCase().includes(searches.scanners.toLowerCase()));
      const matchesBranch = branchFilters.scanners === 'all' || s.branch_id === branchFilters.scanners;
      return matchesSearch && matchesBranch;
    }), [scanners, searches.scanners, branchFilters.scanners]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading master data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk' }} data-testid="master-data-title">
          Master Data Management
        </h1>
        <p className="text-slate-600 mt-1">Manage branches, theaters, items, and pricing</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="branches">Branches</TabsTrigger>
          <TabsTrigger value="theaters">Theaters</TabsTrigger>
          <TabsTrigger value="slots" onClick={fetchTheaterSlots}>Time Slots</TabsTrigger>
          <TabsTrigger value="cakes">Cakes</TabsTrigger>
          <TabsTrigger value="decorations">Decorations</TabsTrigger>
          <TabsTrigger value="food">Food & Beverages</TabsTrigger>
          <TabsTrigger value="scanners">Scanners</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Branches Tab */}
        <TabsContent value="branches">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Branches ({filteredBranches.length})</CardTitle>
              <div className="flex gap-2">
                <Button onClick={exportBranches} variant="outline" size="sm" className="gap-2">
                  <Download size={16} /> Export
                </Button>
                <Dialog open={branchDialog} onOpenChange={setBranchDialog}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setBranchForm({ 
                      id: '', 
                      name: '', 
                      location: '', 
                      google_review_link: '', 
                      state: '', 
                      gstin: '', 
                      branch_code: '',
                      company_name: '',
                      company_address: '',
                      company_phone: '',
                      company_email: ''
                    })} className="gap-2">
                      <Plus size={16} /> Add Branch
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{branchForm.id ? 'Edit' : 'Add'} Branch</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Branch Name *</Label>
                          <Input value={branchForm.name} onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })} required />
                        </div>
                        <div className="space-y-2">
                          <Label>Location *</Label>
                          <Input value={branchForm.location} onChange={(e) => setBranchForm({ ...branchForm, location: e.target.value })} required />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Branch Code *</Label>
                          <Input 
                            value={branchForm.branch_code || ''} 
                            onChange={(e) => setBranchForm({ ...branchForm, branch_code: e.target.value })} 
                            placeholder="e.g., BR001, MUM1"
                          />
                          <p className="text-xs text-slate-500">Used in GST bill numbering</p>
                        </div>
                        <div className="space-y-2">
                          <Label>State *</Label>
                          <Select 
                            value={branchForm.state || ''} 
                            onValueChange={(value) => setBranchForm({ ...branchForm, state: value })}
                          >
                            <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Andhra Pradesh">Andhra Pradesh</SelectItem>
                              <SelectItem value="Arunachal Pradesh">Arunachal Pradesh</SelectItem>
                              <SelectItem value="Assam">Assam</SelectItem>
                              <SelectItem value="Bihar">Bihar</SelectItem>
                              <SelectItem value="Chhattisgarh">Chhattisgarh</SelectItem>
                              <SelectItem value="Goa">Goa</SelectItem>
                              <SelectItem value="Gujarat">Gujarat</SelectItem>
                              <SelectItem value="Haryana">Haryana</SelectItem>
                              <SelectItem value="Himachal Pradesh">Himachal Pradesh</SelectItem>
                              <SelectItem value="Jharkhand">Jharkhand</SelectItem>
                              <SelectItem value="Karnataka">Karnataka</SelectItem>
                              <SelectItem value="Kerala">Kerala</SelectItem>
                              <SelectItem value="Madhya Pradesh">Madhya Pradesh</SelectItem>
                              <SelectItem value="Maharashtra">Maharashtra</SelectItem>
                              <SelectItem value="Manipur">Manipur</SelectItem>
                              <SelectItem value="Meghalaya">Meghalaya</SelectItem>
                              <SelectItem value="Mizoram">Mizoram</SelectItem>
                              <SelectItem value="Nagaland">Nagaland</SelectItem>
                              <SelectItem value="Odisha">Odisha</SelectItem>
                              <SelectItem value="Punjab">Punjab</SelectItem>
                              <SelectItem value="Rajasthan">Rajasthan</SelectItem>
                              <SelectItem value="Sikkim">Sikkim</SelectItem>
                              <SelectItem value="Tamil Nadu">Tamil Nadu</SelectItem>
                              <SelectItem value="Telangana">Telangana</SelectItem>
                              <SelectItem value="Tripura">Tripura</SelectItem>
                              <SelectItem value="Uttar Pradesh">Uttar Pradesh</SelectItem>
                              <SelectItem value="Uttarakhand">Uttarakhand</SelectItem>
                              <SelectItem value="West Bengal">West Bengal</SelectItem>
                              <SelectItem value="Delhi">Delhi</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>GSTIN *</Label>
                        <Input 
                          value={branchForm.gstin || ''} 
                          onChange={(e) => setBranchForm({ ...branchForm, gstin: e.target.value })} 
                          placeholder="22AAAAA0000A1Z5"
                          maxLength={15}
                        />
                        <p className="text-xs text-slate-500">15-character GST Identification Number</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Google Review Link</Label>
                        <Input 
                          value={branchForm.google_review_link || ''} 
                          onChange={(e) => setBranchForm({ ...branchForm, google_review_link: e.target.value })} 
                          placeholder="Paste Google review link here"
                        />
                        <p className="text-xs text-slate-500">Optional - Used for WhatsApp review requests</p>
                      </div>
                      
                      {/* Company Details Section */}
                      <div className="border-t pt-4 mt-4">
                        <h3 className="font-semibold text-sm mb-3 text-slate-700">Company Details (for GST Invoice)</h3>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label>Company Name</Label>
                            <Input 
                              value={branchForm.company_name || ''} 
                              onChange={(e) => setBranchForm({ ...branchForm, company_name: e.target.value })} 
                              placeholder="e.g., Happy Screens Entertainment Pvt Ltd"
                            />
                            <p className="text-xs text-slate-500">Displays on GST invoice header</p>
                          </div>
                          
                          <div className="space-y-2">
                            <Label>Company Address</Label>
                            <Input 
                              value={branchForm.company_address || ''} 
                              onChange={(e) => setBranchForm({ ...branchForm, company_address: e.target.value })} 
                              placeholder="Full address including city, state, pincode"
                            />
                            <p className="text-xs text-slate-500">Full address for GST invoice</p>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Company Phone</Label>
                              <Input 
                                value={branchForm.company_phone || ''} 
                                onChange={(e) => setBranchForm({ ...branchForm, company_phone: e.target.value })} 
                                placeholder="Contact number"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Company Email</Label>
                              <Input 
                                type="email"
                                value={branchForm.company_email || ''} 
                                onChange={(e) => setBranchForm({ ...branchForm, company_email: e.target.value })} 
                                placeholder="contact@company.com"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <Button onClick={saveBranch} className="w-full"><Save size={16} className="mr-2" /> Save</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <Input
                    placeholder="Search branches..."
                    value={searches.branches}
                    onChange={(e) => setSearches({ ...searches, branches: e.target.value })}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="space-y-2">
                {filteredBranches.map(branch => (
                  <div key={branch.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{branch.name}</p>
                      <p className="text-sm text-slate-600">{branch.location}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => { setBranchForm(branch); setBranchDialog(true); }}>
                        <Edit size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteBranch(branch.id)} className="text-red-600">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Theaters Tab */}
        <TabsContent value="theaters">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Theaters ({filteredTheaters.length})</CardTitle>
              <div className="flex gap-2">
                <Button onClick={exportTheaters} variant="outline" size="sm" className="gap-2">
                  <Download size={16} /> Export
                </Button>
                <Dialog open={theaterDialog} onOpenChange={setTheaterDialog}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setTheaterForm({ id: '', name: '', branch_id: '', branch_name: '', min_capacity: 2, price: 0, extra_person_charge: 0 })} className="gap-2">
                      <Plus size={16} /> Add Theater
                    </Button>
                  </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{theaterForm.id ? 'Edit' : 'Add'} Theater</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Theater Name *</Label>
                      <Input value={theaterForm.name} onChange={(e) => setTheaterForm({ ...theaterForm, name: e.target.value })} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Branch *</Label>
                      <Select value={theaterForm.branch_id} onValueChange={(value) => setTheaterForm({ ...theaterForm, branch_id: value })}>
                        <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                        <SelectContent>
                          {branches.map(branch => (
                            <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Min Capacity *</Label>
                        <Input type="number" value={theaterForm.min_capacity} onChange={(e) => setTheaterForm({ ...theaterForm, min_capacity: parseInt(e.target.value) })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Base Price *</Label>
                        <Input type="number" step="0.01" value={theaterForm.price} onChange={(e) => setTheaterForm({ ...theaterForm, price: parseFloat(e.target.value) })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Extra Person Charge *</Label>
                        <Input type="number" step="0.01" value={theaterForm.extra_person_charge} onChange={(e) => setTheaterForm({ ...theaterForm, extra_person_charge: parseFloat(e.target.value) })} required />
                      </div>
                    </div>
                    <Button onClick={saveTheater} className="w-full"><Save size={16} className="mr-2" /> Save</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                    <Input
                      placeholder="Search theaters..."
                      value={searches.theaters}
                      onChange={(e) => setSearches({ ...searches, theaters: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Filter by Branch</Label>
                  <Select value={branchFilters.theaters} onValueChange={(value) => setBranchFilters({ ...branchFilters, theaters: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All branches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All branches</SelectItem>
                      {branches.map(branch => (
                        <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                {filteredTheaters.map(theater => (
                  <div key={theater.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{theater.name}</p>
                      <p className="text-sm text-slate-600">{theater.branch_name}</p>
                      <p className="text-xs text-slate-500">Capacity: {theater.min_capacity} | Price: ₹{theater.price} | Extra: ₹{theater.extra_person_charge}/person</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => { setTheaterForm(theater); setTheaterDialog(true); }}>
                        <Edit size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteTheater(theater.id)} className="text-red-600">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Time Slots Tab */}
        <TabsContent value="slots">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Theater Time Slots ({filteredSlots.length})</CardTitle>
              <Dialog open={slotDialog} onOpenChange={setSlotDialog}>
                <DialogTrigger asChild>
                  <Button onClick={() => setSlotForm({ id: '', theater_name: '', time_slot: '' })} className="gap-2">
                    <Plus size={16} /> Add Time Slot
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Time Slot</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Theater *</Label>
                      <Select value={slotForm.theater_name} onValueChange={(value) => setSlotForm({ ...slotForm, theater_name: value })}>
                        <SelectTrigger><SelectValue placeholder="Select theater" /></SelectTrigger>
                        <SelectContent>
                          {theaters.map(theater => (
                            <SelectItem key={theater.id} value={theater.name}>{theater.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Time Slot (e.g., 09:00 AM - 12:00 PM) *</Label>
                      <Input value={slotForm.time_slot} onChange={(e) => setSlotForm({ ...slotForm, time_slot: e.target.value })} placeholder="09:00 AM - 12:00 PM" required />
                    </div>
                    <Button onClick={saveSlot} className="w-full"><Save size={16} className="mr-2" /> Save</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                    <Input
                      placeholder="Search slots..."
                      value={searches.slots}
                      onChange={(e) => setSearches({ ...searches, slots: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Filter by Branch</Label>
                  <Select value={branchFilters.slots} onValueChange={(value) => setBranchFilters({ ...branchFilters, slots: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All branches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All branches</SelectItem>
                      {branches.map(branch => (
                        <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                {filteredSlots.map(slot => (
                  <div key={slot.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{slot.theater_name}</p>
                      <p className="text-sm text-slate-600">{slot.time_slot}</p>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => deleteSlot(slot.id)} className="text-red-600">
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Similar tabs for Cakes, Decorations, Food & Beverages continue with similar patterns */}
        {/* For brevity, I'll create simplified versions */}

        <TabsContent value="cakes">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Cakes ({filteredCakes.length})</CardTitle>
              <div className="flex gap-2">
                <Button onClick={exportCakes} variant="outline" size="sm" className="gap-2">
                  <Download size={16} /> Export
                </Button>
                <Dialog open={cakeDialog} onOpenChange={setCakeDialog}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setCakeForm({ id: '', name: '', branch_id: '', branch_name: '', price: 0 })} className="gap-2">
                      <Plus size={16} /> Add Cake
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{cakeForm.id ? 'Edit' : 'Add'} Cake</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Cake Name *</Label>
                        <Input value={cakeForm.name} onChange={(e) => setCakeForm({ ...cakeForm, name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Branch *</Label>
                        <Select value={cakeForm.branch_id} onValueChange={(value) => setCakeForm({ ...cakeForm, branch_id: value })}>
                          <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                          <SelectContent>
                            {branches.map(branch => (
                              <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Price *</Label>
                        <Input type="number" step="0.01" value={cakeForm.price} onChange={(e) => setCakeForm({ ...cakeForm, price: parseFloat(e.target.value) })} required />
                      </div>
                      <Button onClick={saveCake} className="w-full"><Save size={16} className="mr-2" /> Save</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                    <Input
                      placeholder="Search cakes..."
                      value={searches.cakes}
                      onChange={(e) => setSearches({ ...searches, cakes: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Filter by Branch</Label>
                  <Select value={branchFilters.cakes} onValueChange={(value) => setBranchFilters({ ...branchFilters, cakes: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All branches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All branches</SelectItem>
                      {branches.map(branch => (
                        <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                {filteredCakes.map(cake => (
                  <div key={cake.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{cake.name}</p>
                      <p className="text-sm text-slate-600">{cake.branch_name} • ₹{cake.price}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => { setCakeForm(cake); setCakeDialog(true); }}>
                        <Edit size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteCake(cake.id)} className="text-red-600">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="decorations">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Decorations ({filteredDecorations.length})</CardTitle>
              <div className="flex gap-2">
                <Button onClick={exportDecorations} variant="outline" size="sm" className="gap-2">
                  <Download size={16} /> Export
                </Button>
                <Dialog open={decorationDialog} onOpenChange={setDecorationDialog}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setDecorationForm({ id: '', name: '', branch_id: '', branch_name: '', price: 0 })} className="gap-2">
                      <Plus size={16} /> Add Decoration
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{decorationForm.id ? 'Edit' : 'Add'} Decoration</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Decoration Name *</Label>
                        <Input value={decorationForm.name} onChange={(e) => setDecorationForm({ ...decorationForm, name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Branch *</Label>
                        <Select value={decorationForm.branch_id} onValueChange={(value) => setDecorationForm({ ...decorationForm, branch_id: value })}>
                          <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                          <SelectContent>
                            {branches.map(branch => (
                              <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Price *</Label>
                        <Input type="number" step="0.01" value={decorationForm.price} onChange={(e) => setDecorationForm({ ...decorationForm, price: parseFloat(e.target.value) })} required />
                      </div>
                      <Button onClick={saveDecoration} className="w-full"><Save size={16} className="mr-2" /> Save</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                    <Input
                      placeholder="Search decorations..."
                      value={searches.decorations}
                      onChange={(e) => setSearches({ ...searches, decorations: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Filter by Branch</Label>
                  <Select value={branchFilters.decorations} onValueChange={(value) => setBranchFilters({ ...branchFilters, decorations: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All branches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All branches</SelectItem>
                      {branches.map(branch => (
                        <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                {filteredDecorations.map(decoration => (
                  <div key={decoration.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{decoration.name}</p>
                      <p className="text-sm text-slate-600">{decoration.branch_name} • ₹{decoration.price}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => { setDecorationForm(decoration); setDecorationDialog(true); }}>
                        <Edit size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteDecoration(decoration.id)} className="text-red-600">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="food">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Food & Beverages ({filteredFoodItems.length})</CardTitle>
              <div className="flex gap-2">
                <Button onClick={exportFood} variant="outline" size="sm" className="gap-2">
                  <Download size={16} /> Export
                </Button>
                <Dialog open={importDialog} onOpenChange={setImportDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Upload size={16} /> Import
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Import Food & Beverages Items</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Select Branch *</Label>
                        <Select value={importBranch} onValueChange={setImportBranch}>
                          <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                          <SelectContent>
                            {branches.map(branch => (
                              <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Paste Items (Format: Name, Price)</Label>
                        <p className="text-xs text-slate-500">Paste one item per line. Example: Veg Spring Roll, 99</p>
                        <textarea
                          value={importText}
                          onChange={(e) => setImportText(e.target.value)}
                          placeholder="Veg Spring Roll, 99&#10;Paneer Momos, 109&#10;Cold Coffee, 79"
                          className="w-full h-64 p-3 border rounded-md font-mono text-sm"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={handleImport} className="flex-1">
                          <Upload size={16} className="mr-2" /> Import {importText.split('\n').filter(l => l.trim()).length} Items
                        </Button>
                        <Button onClick={() => setImportDialog(false)} variant="outline">Cancel</Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                <Dialog open={foodDialog} onOpenChange={setFoodDialog}>
                  <DialogTrigger asChild>
                    <Button onClick={() => setFoodForm({ id: '', name: '', branch_id: '', branch_name: '', price: 0 })} className="gap-2">
                      <Plus size={16} /> Add Item
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{foodForm.id ? 'Edit' : 'Add'} Food & Beverage</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Item Name *</Label>
                        <Input value={foodForm.name} onChange={(e) => setFoodForm({ ...foodForm, name: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>Branch *</Label>
                        <Select value={foodForm.branch_id} onValueChange={(value) => setFoodForm({ ...foodForm, branch_id: value })}>
                          <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                          <SelectContent>
                            {branches.map(branch => (
                              <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Price *</Label>
                        <Input type="number" step="0.01" value={foodForm.price} onChange={(e) => setFoodForm({ ...foodForm, price: parseFloat(e.target.value) })} required />
                      </div>
                      <Button onClick={saveFoodItem} className="w-full"><Save size={16} className="mr-2" /> Save</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                    <Input
                      placeholder="Search food items..."
                      value={searches.food}
                      onChange={(e) => setSearches({ ...searches, food: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Filter by Branch</Label>
                  <Select value={branchFilters.food} onValueChange={(value) => setBranchFilters({ ...branchFilters, food: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All branches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All branches</SelectItem>
                      {branches.map(branch => (
                        <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                {filteredFoodItems.map(food => (
                  <div key={food.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{food.name}</p>
                      <p className="text-sm text-slate-600">{food.branch_name} • ₹{food.price}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => { setFoodForm(food); setFoodDialog(true); }}>
                        <Edit size={16} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteFoodItem(food.id)} className="text-red-600">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scanners">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Payment Scanners ({filteredScanners.length})</CardTitle>
              <Dialog open={scannerDialog} onOpenChange={setScannerDialog}>
                <DialogTrigger asChild>
                  <Button onClick={() => setScannerForm({ id: '', name: '', branch_id: '', branch_name: '' })} className="gap-2">
                    <Plus size={16} /> Add Scanner
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{scannerForm.id ? 'Edit' : 'Add'} Scanner</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Scanner Name *</Label>
                      <Input value={scannerForm.name} onChange={(e) => setScannerForm({ ...scannerForm, name: e.target.value })} placeholder="e.g., Scanner 1, GPay, Paytm" required />
                    </div>
                    <div className="space-y-2">
                      <Label>Branch *</Label>
                      <Select value={scannerForm.branch_id} onValueChange={(value) => setScannerForm({ ...scannerForm, branch_id: value })}>
                        <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                        <SelectContent>
                          {branches.map(branch => (
                            <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={saveScanner} className="w-full"><Save size={16} className="mr-2" /> Save</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                    <Input
                      placeholder="Search scanners..."
                      value={searches.scanners}
                      onChange={(e) => setSearches({ ...searches, scanners: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Filter by Branch</Label>
                  <Select value={branchFilters.scanners} onValueChange={(value) => setBranchFilters({ ...branchFilters, scanners: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="All branches" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All branches</SelectItem>
                      {branches.map(branch => (
                        <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                {filteredScanners.map(scanner => (
                  <div key={scanner.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{scanner.name}</p>
                      <p className="text-sm text-slate-600">
                        {scanner.branch_name || <span className="text-orange-600">⚠️ Not assigned to branch</span>}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => { setScannerForm(scanner); setScannerDialog(true); }}>
                        <Edit size={16} />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          alert('Delete button clicked for: ' + scanner.name);
                          deleteScanner(scanner.id);
                        }} 
                        className="text-red-600"
                        data-testid={`delete-scanner-${scanner.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Application Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reviewMessageTemplate" className="text-base font-semibold">
                    Review Request Message Template
                  </Label>
                  <p className="text-sm text-slate-600">
                    This message will be sent via WhatsApp when requesting reviews. Use placeholders:
                  </p>
                  <div className="text-xs bg-slate-100 p-3 rounded-md space-y-1">
                    <p><code className="bg-white px-2 py-1 rounded">{'{branch_name}'}</code> - Branch name (e.g., "Banashankari")</p>
                    <p><code className="bg-white px-2 py-1 rounded">{'{theater_name}'}</code> - Theater name (e.g., "Theater 1")</p>
                    <p><code className="bg-white px-2 py-1 rounded">{'{review_link}'}</code> - Google review link</p>
                  </div>
                  <textarea
                    id="reviewMessageTemplate"
                    value={reviewMessageTemplate}
                    onChange={(e) => setReviewMessageTemplate(e.target.value)}
                    rows={8}
                    className="w-full p-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter your custom message template..."
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-blue-900 mb-2">Preview:</p>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap bg-white p-3 rounded border">
                    {reviewMessageTemplate
                      .replace(/{branch_name}/g, '[Branch Name]')
                      .replace(/{theater_name}/g, '[Theater Name]')
                      .replace(/{review_link}/g, '[Google Review Link]')}
                  </div>
                </div>

                <Button onClick={saveSettings} className="w-full md:w-auto">
                  <Save size={16} className="mr-2" /> Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
